/**
 * Public gym profile page — no auth required.
 * URL: /gym/{id}
 * Shows gym name, address, member count, average strength score, top lifters, gym rank.
 */

import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Share, Platform } from 'react-native';
import { useLocalSearchParams, useRouter, Stack } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { TYPO } from '@/constants/typography';
import { SPACE, FONT } from '@/constants/designTokens';
import { MapPin, Users, Trophy, TrendingUp, Award, Share2 } from 'lucide-react-native';

interface GymData {
  id: string;
  name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
}

interface TopLifter {
  id: string;
  display_name: string | null;
  handle: string | null;
  duel_standard_strength_kg: number | null;
  avatar_url: string | null;
}

export default function GymProfilePage() {
  const { theme } = useTheme();
  const router = useRouter();
  const { id } = useLocalSearchParams();
  const gymId = typeof id === 'string' ? id : '';
  const c = theme.colors;

  const [gym, setGym] = useState<GymData | null>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [averageStrength, setAverageStrength] = useState<number | null>(null);
  const [topLifters, setTopLifters] = useState<TopLifter[]>([]);
  const [gymRank, setGymRank] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gymId) return;
    (async () => {
      try {
        // Fetch gym info
        const { data: gymData } = await supabase
          .from('gyms')
          .select('id, name, address, city, state')
          .eq('id', gymId)
          .single();

        if (gymData) setGym(gymData as GymData);

        // Fetch member count and average strength score
        // FIX: Include both gym_id and home_gym_id for proper member count
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, duel_standard_strength_kg')
          .or(`gym_id.eq.${gymId},home_gym_id.eq.${gymId}`);

        if (profilesData) {
          setMemberCount(profilesData.length);
          
          // Calculate average strength score
          const validScores = profilesData.filter(p => 
            typeof p.duel_standard_strength_kg === 'number' && 
            isFinite(p.duel_standard_strength_kg) && 
            p.duel_standard_strength_kg > 0
          );
          
          if (validScores.length > 0) {
            const avgScore = validScores.reduce((sum, p) => sum + p.duel_standard_strength_kg!, 0) / validScores.length;
            setAverageStrength(avgScore);
          }
        }

        // Fetch top 5 lifters
        const { data: lifters } = await supabase
          .from('profiles')
          .select('id, display_name, handle, duel_standard_strength_kg, avatar_url')
          .or(`gym_id.eq.${gymId},home_gym_id.eq.${gymId}`)
          .not('duel_standard_strength_kg', 'is', null)
          .gt('duel_standard_strength_kg', 0)
          .order('duel_standard_strength_kg', { ascending: false })
          .limit(5);

        if (lifters) setTopLifters(lifters as TopLifter[]);

        // Calculate gym rank (simplified - rank by average strength)
        if (averageStrength !== null) {
          const { data: gymAvgs } = await supabase
            .rpc('get_gym_average_strengths')
            .gt('avg_strength', averageStrength);
          
          if (gymAvgs) {
            setGymRank(gymAvgs.length + 1);
          }
        }

      } catch (error) {
        console.error('Error loading gym data:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [gymId, averageStrength]);

  if (loading) {
    return (
      <View style={[styles.center, { backgroundColor: c.background }]}>
        <Stack.Screen options={{ title: 'Loading...', headerShown: true }} />
        <ActivityIndicator size="large" color={c.accent} />
      </View>
    );
  }

  if (!gym) {
    return (
      <View style={[styles.center, { backgroundColor: c.background }]}>
        <Stack.Screen options={{ title: 'Gym Not Found', headerShown: true }} />
        <Text style={[styles.errorText, { color: c.textSecondary }]}>
          Gym not found
        </Text>
      </View>
    );
  }

  const locationText = [gym.address, gym.city, gym.state].filter(Boolean).join(', ');

  return (
    <>
      <Stack.Screen 
        options={{ 
          title: gym.name, 
          headerShown: true,
          headerStyle: { backgroundColor: c.background },
          headerTitleStyle: { color: c.text, fontFamily: TYPO.heading.bold },
          headerTintColor: c.accent
        }} 
      />
      <ScrollView
        style={{ flex: 1, backgroundColor: c.background }}
        contentContainerStyle={styles.content}
      >
        {/* Gym Header */}
        <View style={[styles.headerCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.gymName, { color: c.text }]}>{gym.name}</Text>
          
          {locationText && (
            <View style={styles.locationRow}>
              <MapPin size={16} color={c.textSecondary} />
              <Text style={[styles.locationText, { color: c.textSecondary }]}>{locationText}</Text>
            </View>
          )}

          {/* Gym Rank Badge */}
          {gymRank && (
            <View style={[styles.rankBadge, { backgroundColor: c.accent }]}>
              <Award size={16} color="#000" />
              <Text style={[styles.rankText, { color: "#000" }]}>#{gymRank} GYM</Text>
            </View>
          )}
        </View>

        {/* Stats Grid */}
        <View style={styles.statsGrid}>
          <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Users size={20} color={c.accent} />
            <Text style={[styles.statValue, { color: c.text }]}>{memberCount}</Text>
            <Text style={[styles.statLabel, { color: c.textSecondary }]}>MEMBERS</Text>
          </View>

          {averageStrength && (
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <TrendingUp size={20} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>{Math.round(averageStrength)}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>AVG STRENGTH</Text>
            </View>
          )}

          {gymRank && (
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Trophy size={20} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>#{gymRank}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>GYM RANK</Text>
            </View>
          )}
        </View>

        {/* Top Lifters Leaderboard */}
        {topLifters.length > 0 && (
          <View style={[styles.leaderboardSection, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.sectionTitle, { color: c.textTertiary }]}>TOP 5 LIFTERS</Text>
            {topLifters.map((lifter, i) => (
              <TouchableOpacity
                key={lifter.id}
                style={[styles.lifterRow, { borderBottomColor: c.border }]}
                onPress={() => router.push(`/u/${lifter.handle}` as any)}
                activeOpacity={0.7}
              >
                <View style={[styles.rankCircle, { backgroundColor: c.accent }]}>
                  <Text style={[styles.lifterRank, { color: "#000" }]}>#{i + 1}</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.lifterName, { color: c.text }]}>
                    {lifter.display_name || lifter.handle || 'Member'}
                  </Text>
                  {lifter.handle && (
                    <Text style={[styles.lifterHandle, { color: c.textTertiary }]}>@{lifter.handle}</Text>
                  )}
                </View>
                <Text style={[styles.lifterScore, { color: c.accent }]}>
                  {lifter.duel_standard_strength_kg ? Math.round(lifter.duel_standard_strength_kg) : '—'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Invite CTA */}
        <TouchableOpacity
          style={[styles.joinButton, { backgroundColor: c.accent }]}
          onPress={async () => {
            const url = Platform.OS === 'web'
              ? `${window.location.origin}/gym/${gymId}`
              : `https://app.douevenlift.com/gym/${gymId}`;
            const message = `Join me at ${gym?.name ?? 'my gym'} on DUEL! 💪\n${url}`;
            try {
              await Share.share({ message, url });
            } catch {}
          }}
          activeOpacity={0.8}
        >
          <Share2 size={18} color="#000" style={{ marginRight: 8 }} />
          <Text style={[styles.joinButtonText, { color: "#000" }]}>INVITE A FRIEND</Text>
        </TouchableOpacity>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  center: { 
    flex: 1, 
    alignItems: 'center', 
    justifyContent: 'center' 
  },
  content: { 
    padding: SPACE.lg,
    paddingBottom: SPACE.xl * 2,
    gap: SPACE.lg 
  },
  errorText: {
    fontSize: FONT.lg,
    fontFamily: TYPO.body.regular,
    textAlign: 'center'
  },
  headerCard: {
    borderWidth: 1,
    borderRadius: 4,
    padding: SPACE.lg,
    alignItems: 'center',
    gap: SPACE.sm
  },
  gymName: {
    fontSize: FONT['3xl'],
    fontFamily: TYPO.heading.bold,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 2
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.xs
  },
  locationText: {
    fontSize: FONT.md,
    fontFamily: TYPO.body.regular
  },
  rankBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.xs,
    paddingHorizontal: SPACE.sm,
    paddingVertical: SPACE.xs,
    borderRadius: 4,
    marginTop: SPACE.sm
  },
  rankText: {
    fontSize: FONT.sm,
    fontFamily: TYPO.subheading.bold,
    letterSpacing: 1
  },
  statsGrid: {
    flexDirection: 'row',
    gap: SPACE.sm,
    flexWrap: 'wrap'
  },
  statCard: {
    flex: 1,
    minWidth: 100,
    borderWidth: 1,
    borderRadius: 4,
    padding: SPACE.md,
    alignItems: 'center',
    gap: SPACE.xs
  },
  statValue: {
    fontSize: FONT.xl,
    fontFamily: TYPO.heading.bold
  },
  statLabel: {
    fontSize: FONT.xs,
    fontFamily: TYPO.subheading.bold,
    letterSpacing: 1,
    textAlign: 'center'
  },
  leaderboardSection: {
    borderWidth: 1,
    borderRadius: 4,
    padding: SPACE.lg
  },
  sectionTitle: {
    fontSize: FONT.sm,
    fontFamily: TYPO.subheading.bold,
    letterSpacing: 1,
    marginBottom: SPACE.md,
    textAlign: 'center'
  },
  lifterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACE.sm,
    paddingHorizontal: SPACE.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: SPACE.sm
  },
  rankCircle: {
    width: 32,
    height: 32,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center'
  },
  lifterRank: {
    fontSize: FONT.sm,
    fontFamily: TYPO.subheading.bold
  },
  lifterName: {
    fontSize: FONT.md,
    fontFamily: TYPO.heading.bold
  },
  lifterHandle: {
    fontSize: FONT.sm,
    fontFamily: TYPO.subheading.regular,
    marginTop: 2
  },
  lifterScore: {
    fontSize: FONT.lg,
    fontFamily: TYPO.heading.bold
  },
  joinButton: {
    backgroundColor: '#DAA520',
    paddingVertical: SPACE.lg,
    paddingHorizontal: SPACE.xl,
    borderRadius: 0,
    marginTop: SPACE.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  joinButtonText: {
    fontSize: FONT.md,
    fontFamily: TYPO.heading.bold,
    textAlign: 'center',
    letterSpacing: 1
  }
});
